import React, { useState } from "react";
import { CheckCircle2, Circle, PackageSearch } from "lucide-react";
import { api } from "../lib/api";

const STEPS = [
  { id: "confirmed", label: "Order Confirmed" },
  { id: "building", label: "Building Your Frame" },
  { id: "shipped", label: "Shipped" },
  { id: "delivered", label: "Delivered" },
];

export default function TrackOrderPage() {
  const [orderId, setOrderId] = useState("");
  const [email, setEmail] = useState("");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setOrder(null);
    setLoading(true);
    try {
      const { data } = await api.post("/orders/track", {
        order_id: orderId.trim(),
        email: email.trim(),
      });
      setOrder(data);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Could not find that order. Double-check the details.");
    } finally {
      setLoading(false);
    }
  };

  const stepIndex = order ? STEPS.findIndex((s) => s.id === order.order_status) : -1;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white px-6 md:px-12 py-16">
      <div className="max-w-2xl mx-auto">
        <p className="af-mono text-[10px] uppercase tracking-[0.25em] text-[#FF2800]">
          // Pit Wall Telemetry
        </p>
        <h1 className="af-display text-4xl sm:text-5xl font-black uppercase tracking-tight mt-2">
          Track Your Order
        </h1>
        <p className="text-zinc-400 mt-3 text-sm">
          Enter your Order ID (starts with "order_", from your checkout confirmation) and the
          email you used at checkout.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="track-order-form">
          <input
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            placeholder="order_XXXXXXXXXXXX"
            required
            className="w-full bg-black border border-white/15 px-4 py-3 text-sm focus:outline-none focus:border-[#FF2800] transition-colors placeholder:text-zinc-700 af-mono"
            data-testid="track-order-id-input"
          />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
            className="w-full bg-black border border-white/15 px-4 py-3 text-sm focus:outline-none focus:border-[#FF2800] transition-colors placeholder:text-zinc-700"
            data-testid="track-email-input"
          />
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center gap-2 bg-[#FF2800] text-white font-semibold uppercase tracking-wider px-8 py-3.5 hover:bg-[#CC2000] disabled:opacity-50 transition-colors text-sm"
            data-testid="track-submit-button"
          >
            <PackageSearch size={16} />
            {loading ? "Checking…" : "Track order"}
          </button>
        </form>

        {error && (
          <p className="mt-6 text-[#FF2800] text-sm" data-testid="track-error">
            {error}
          </p>
        )}

        {order && (
          <div className="mt-10 border border-white/10 bg-[#0E0E0E] p-6 md:p-8" data-testid="track-result">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <p className="af-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  Order
                </p>
                <p className="af-mono text-sm break-all">{order.order_id}</p>
              </div>
              <div className="text-right">
                <p className="af-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  Total
                </p>
                <p className="af-display text-xl font-bold">
                  ₹{Number(order.amount).toLocaleString("en-IN")}
                </p>
              </div>
            </div>

            <div className="mt-8 space-y-0" data-testid="track-steps">
              {STEPS.map((s, i) => {
                const done = i <= stepIndex;
                const current = i === stepIndex;
                return (
                  <div key={s.id} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      {done ? (
                        <CheckCircle2 size={22} className="text-[#FF2800]" />
                      ) : (
                        <Circle size={22} className="text-zinc-700" />
                      )}
                      {i < STEPS.length - 1 && (
                        <div
                          className={`w-px h-10 ${i < stepIndex ? "bg-[#FF2800]" : "bg-zinc-800"}`}
                        />
                      )}
                    </div>
                    <div className="pb-6">
                      <p
                        className={`font-semibold text-sm uppercase tracking-wider ${
                          done ? "text-white" : "text-zinc-600"
                        }`}
                      >
                        {s.label}
                        {current && (
                          <span className="ml-2 af-mono text-[9px] text-[#FF2800] tracking-[0.2em]">
                            ● CURRENT
                          </span>
                        )}
                      </p>
                      {s.id === "shipped" && done && order.tracking_number && (
                        <p className="text-zinc-400 text-xs mt-1 af-mono" data-testid="track-awb">
                          {order.courier ? `${order.courier} · ` : ""}Tracking #: {order.tracking_number}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="border-t border-white/10 pt-4 mt-2">
              <p className="af-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
                Items
              </p>
              {order.items.map((it, i) => (
                <p key={i} className="text-sm text-zinc-300">
                  {it.name} × {it.qty}
                  {it.options_label ? <span className="text-zinc-500"> · {it.options_label}</span> : null}
                </p>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
